import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { MarkdownContent } from '@/components/MarkdownContent';
import { useCreateAnnouncementMutation, useUpdateAnnouncementMutation } from '@/store/announcementsApi';
import { Announcement, AnnouncementPayload, AnnouncementType } from '@/types/Announcement';

interface Props {
    open: boolean;
    onOpenChange: (v: boolean) => void;
    initialData: Announcement | null;
    /** טיוטה שנוצרה ע"י AI, למילוי מקדים במצב יצירה בלבד (מתעלמים ממנה אם קיים initialData) */
    initialDraft?: Pick<Announcement, 'type' | 'desc' | 'long'> | null;
    /** ערך ברירת מחדל לשדה "סדר תצוגה" בעת יצירת עדכון חדש (בד"כ המספר הגבוה ביותר בעדכונים הקיימים + 1) */
    defaultOrder?: number;
}

const EMPTY_FORM: AnnouncementPayload = {
    type: 'NEWS',
    desc: '',
    long: '',
    url: '',
    is_active: true,
    order: 0,
    valid_until: null,
};

/** ISO string <-> ערך input מסוג datetime-local */
function isoToLocalInput(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(value: string): string | null {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function AnnouncementFormModal({ open, onOpenChange, initialData, initialDraft, defaultOrder }: Props) {
    const isEdit = !!initialData;
    const [createAnnouncement, { isLoading: isCreating }] = useCreateAnnouncementMutation();
    const [updateAnnouncement, { isLoading: isUpdating }] = useUpdateAnnouncementMutation();
    const isSubmitting = isCreating || isUpdating;

    const [form, setForm] = useState<AnnouncementPayload>(EMPTY_FORM);
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        if (open) {
            if (initialData) {
                setForm({
                    type: initialData.type,
                    desc: initialData.desc ?? '',
                    long: initialData.long ?? '',
                    url: initialData.url ?? '',
                    is_active: initialData.is_active,
                    order: initialData.order,
                    valid_until: initialData.valid_until,
                });
            } else {
                setForm({ ...EMPTY_FORM, order: defaultOrder ?? EMPTY_FORM.order, ...(initialDraft ?? {}) });
            }
            setShowPreview(false);
        }
    }, [open, initialData, initialDraft, defaultOrder]);

    function set<K extends keyof AnnouncementPayload>(key: K, value: AnnouncementPayload[K]) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        try {
            if (isEdit) {
                await updateAnnouncement({ id: initialData!.id, ...form }).unwrap();
                toast.success('ההודעה עודכנה בהצלחה');
            } else {
                await createAnnouncement(form).unwrap();
                toast.success('ההודעה נוצרה בהצלחה');
            }
            onOpenChange(false);
        } catch (err: unknown) {
            const msg = (err as { data?: { detail?: string; msg?: string } })?.data?.detail
                ?? (err as { data?: { detail?: string; msg?: string } })?.data?.msg
                ?? 'שגיאה בשמירה';
            toast.error(msg);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'עריכת הודעה' : 'הודעה חדשה'}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <Label>סוג</Label>
                            <Select value={form.type} onValueChange={(v) => set('type', v as AnnouncementType)}>
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="NEWS">מה חדש</SelectItem>
                                    <SelectItem value="MSG">הודעה</SelectItem>
                                    <SelectItem value="LINK">קישור</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label>סדר תצוגה</Label>
                            <Input
                                type="number"
                                value={form.order ?? 0}
                                onChange={(e) => set('order', Number(e.target.value))}
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <Label>כותרת</Label>
                        <Input
                            value={form.desc ?? ''}
                            onChange={(e) => set('desc', e.target.value)}
                            placeholder="כותרת ההודעה"
                            required
                        />
                    </div>

                    <div className="space-y-1">
                        <Label>קישור (אופציונלי)</Label>
                        <Input
                            value={form.url ?? ''}
                            onChange={(e) => set('url', e.target.value)}
                            placeholder="https://..."
                        />
                    </div>

                    <div className="space-y-1">
                        <div className="flex items-center justify-between">
                            <Label>תוכן (Markdown)</Label>
                            <Button
                                type="button"
                                variant="outline"
                                size="xs"
                                onClick={() => setShowPreview((v) => !v)}
                            >
                                {showPreview ? <Pencil className="h-3.5 w-3.5 ml-1" /> : <Eye className="h-3.5 w-3.5 ml-1" />}
                                {showPreview ? 'עריכה' : 'תצוגה מקדימה'}
                            </Button>
                        </div>
                        {showPreview ? (
                            <div className="min-h-[160px] rounded-md border border-gray-200 bg-gray-50 p-3">
                                <MarkdownContent content={form.long} />
                            </div>
                        ) : (
                            <Textarea
                                value={form.long ?? ''}
                                onChange={(e) => set('long', e.target.value)}
                                rows={7}
                                className="font-mono text-sm"
                                placeholder={'ניתן להשתמש בעיצוב Markdown: **מודגש**, _נטוי_, רשימות וכד\''}
                            />
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <Label>תוקף ההודעה עד (אופציונלי)</Label>
                            <Input
                                type="datetime-local"
                                value={isoToLocalInput(form.valid_until)}
                                onChange={(e) => set('valid_until', localInputToIso(e.target.value))}
                            />
                        </div>
                        <div className="flex items-center gap-2 pt-6">
                            <Switch checked={form.is_active ?? true} onCheckedChange={(v) => set('is_active', v)} />
                            <Label>פעיל</Label>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>ביטול</Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'שומר...' : 'שמור'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
